import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { sensorAPI } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

// Import dashboard components
import StatsCard from '../components/dashboard/StatsCard';
import DataChart from '../components/dashboard/DataChart';
import DataTable from '../components/dashboard/DataTable';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // State
  const [latestData, setLatestData] = useState(null);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch latest reading
      const latestResponse = await sensorAPI.getLatest();
      if (latestResponse.success) {
        setLatestData(latestResponse.data);
        setLastUpdate(new Date());
      }

      // Fetch statistics
      const statsResponse = await sensorAPI.getStats();
      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }

      // Fetch data for chart (last 50 readings)
      const chartResponse = await sensorAPI.getData({ limit: 50, page: 1 });
      if (chartResponse.success) {
        setChartData(chartResponse.data.reverse()); // Reverse for chronological order
      }

      // Fetch data for table (last 100 readings)
      const tableResponse = await sensorAPI.getData({ limit: 100, page: 1 });
      if (tableResponse.success) {
        setTableData(tableResponse.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this reading?')) {
      try {
        await sensorAPI.deleteData(id);
        fetchData(); // Refresh data after deletion
      } catch (error) {
        console.error('Error deleting data:', error);
        alert('Failed to delete data. You may not have admin permissions.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-ocean-dark via-ocean-dark to-ocean-blue/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4"
        >
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
              <svg className="w-10 h-10 mr-3 text-wave-yellow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              IoT Dashboard
            </h1>
            <p className="text-ocean-sky">
              Welcome back, <span className="text-wave-yellow font-medium">{user?.name || 'User'}</span>!
            </p>
            {lastUpdate && (
              <p className="text-ocean-sky/70 text-sm mt-1">
                Last updated: {formatDistanceToNow(lastUpdate, { addSuffix: true })}
              </p>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleRefresh}
              className="btn-secondary flex items-center"
              disabled={loading}
            >
              <svg className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="btn-primary flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Current PM2.5"
            value={latestData ? parseFloat(latestData.pm25).toFixed(2) : null}
            unit="µg/m³"
            color="yellow"
            icon={
              <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
            }
          />

          <StatsCard
            title="Current Temperature"
            value={latestData ? parseFloat(latestData.temp).toFixed(1) : null}
            unit="°C"
            color="blue"
            icon={
              <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
          />

          <StatsCard
            title="Average PM2.5"
            value={stats ? parseFloat(stats.pm25.average) : null}
            unit="µg/m³"
            color="purple"
            icon={
              <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            }
          />

          <StatsCard
            title="Total Readings"
            value={stats ? stats.totalReadings : null}
            color="green"
            icon={
              <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-ocean-light/20">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'overview'
                    ? 'border-wave-yellow text-wave-yellow'
                    : 'border-transparent text-ocean-sky hover:text-white hover:border-ocean-light/50'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('data')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'data'
                    ? 'border-wave-yellow text-wave-yellow'
                    : 'border-transparent text-ocean-sky hover:text-white hover:border-ocean-light/50'
                }`}
              >
                Data Table
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Chart */}
            <div className="bg-ocean-dark/80 backdrop-blur-xl border border-ocean-light/20 rounded-2xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Sensor Data Trends</h2>
                <div className="text-sm text-ocean-sky">
                  Last {chartData.length} readings
                </div>
              </div>
              <DataChart data={chartData} loading={loading} />
            </div>

            {/* Stats Summary */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-ocean-dark/80 backdrop-blur-xl border border-ocean-light/20 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="w-3 h-3 bg-yellow-400 rounded-full mr-2"></span>
                    PM2.5 Statistics
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-ocean-sky">Average:</span>
                      <span className="text-white font-medium">{stats.pm25.average} µg/m³</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ocean-sky">Minimum:</span>
                      <span className="text-white font-medium">{stats.pm25.min} µg/m³</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ocean-sky">Maximum:</span>
                      <span className="text-white font-medium">{stats.pm25.max} µg/m³</span>
                    </div>
                  </div>
                </div>

                <div className="bg-ocean-dark/80 backdrop-blur-xl border border-ocean-light/20 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="w-3 h-3 bg-blue-400 rounded-full mr-2"></span>
                    Temperature Statistics
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-ocean-sky">Average:</span>
                      <span className="text-white font-medium">{stats.temperature.average} °C</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ocean-sky">Minimum:</span>
                      <span className="text-white font-medium">{stats.temperature.min} °C</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ocean-sky">Maximum:</span>
                      <span className="text-white font-medium">{stats.temperature.max} °C</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'data' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-ocean-dark/80 backdrop-blur-xl border border-ocean-light/20 rounded-2xl p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Sensor Data Records</h2>
              <div className="text-sm text-ocean-sky">
                Total: {tableData.length} records
              </div>
            </div>
            <DataTable
              data={tableData}
              loading={loading}
              onDelete={user?.role === 'admin' ? handleDelete : null}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
}
